import { useCallback, useState } from 'react'
import { Package, Plus } from 'lucide-react'

import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  createProduct,
  fetchProductsList,
  fetchProductsPage,
  formatAmount,
  toAmount,
  updateProduct,
} from '@/lib/api/products'
import { cn } from '@/lib/utils'

function emptyForm() {
  return {
    name: '',
    sku: '',
    barcode: '',
    cost: '',
    sell_price: '',
    min_stock: 0,
    is_active: true,
  }
}

/**
 * @param {import('@/types/api').Product} product
 */
function productToForm(product) {
  return {
    name: product.name ?? '',
    sku: product.sku ?? '',
    barcode: product.barcode ?? '',
    cost: toAmount(product.cost),
    sell_price: toAmount(product.sell_price),
    min_stock: product.min_stock ?? 0,
    is_active: Boolean(product.is_active),
  }
}

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  { key: 'sku', label: 'SKU', getValue: (row) => row.sku },
  { key: 'barcode', label: 'Barcode', getValue: (row) => row.barcode ?? '' },
  { key: 'cost', label: 'Cost', getValue: (row) => toAmount(row.cost) },
  { key: 'sell_price', label: 'Sell price', getValue: (row) => toAmount(row.sell_price) },
  { key: 'min_stock', label: 'Min stock', getValue: (row) => row.min_stock },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

export function ProductsPage() {
  const { activeBranchId } = useBranch()
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyForm())
  const [reloadKey, setReloadKey] = useState(0)

  const fetchPage = useCallback((params) => fetchProductsPage(params), [])

  function openCreate() {
    setCreating(true)
    setSelectedId(null)
    setForm(emptyForm())
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  /**
   * @param {import('@/types/api').Product} product
   */
  function openEdit(product) {
    setCreating(false)
    setSelectedId(product.id)
    setForm(productToForm(product))
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting) return
    setModalOpen(false)
    setError('')
    setSuccess('')
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    const payload = {
      name: form.name,
      sku: form.sku,
      barcode: form.barcode || null,
      cost: toAmount(form.cost),
      sell_price: toAmount(form.sell_price),
      min_stock: Number(form.min_stock) || 0,
      is_active: form.is_active,
    }

    try {
      if (creating) {
        await createProduct(payload)
        setSuccess('Product created.')
      } else if (selectedId) {
        await updateProduct(selectedId, payload)
        setSuccess('Product saved.')
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      const message =
        getFieldError(err, 'sku') ??
        getFieldError(err, 'barcode') ??
        getFieldError(err, 'name') ??
        getFieldError(err, 'cost') ??
        getFieldError(err, 'sell_price') ??
        err?.response?.data?.message ??
        'Unable to save product.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Package className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Products</h1>
            <p className="text-sm text-muted-foreground">
              Retail stock — SKU, barcode, cost, sell price, and minimum stock level.
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New product
        </Button>
      </div>

      <MasterServerTable
        key={activeBranchId}
        fetchPage={fetchPage}
        reloadKey={reloadKey}
        getRowKey={(product) => product.id}
        onEdit={openEdit}
        emptyLabel="No products yet."
        searchPlaceholder="Search products…"
        exportColumns={EXPORT_COLUMNS}
        exportFilename="products.csv"
        fetchAllForExport={(params) => fetchProductsList(params)}
        columns={[
          { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
          { key: 'sku', label: 'SKU', sortable: true, render: (row) => row.sku },
          {
            key: 'sell_price',
            label: 'Sell price',
            sortable: true,
            render: (row) => formatAmount(row.sell_price),
          },
          {
            key: 'min_stock',
            label: 'Min stock',
            render: (row) => row.min_stock ?? 0,
          },
          {
            key: 'is_active',
            label: 'Status',
            render: (row) => (row.is_active ? 'Active' : 'Inactive'),
          },
        ]}
      />

      <MasterFormModal
        open={modalOpen}
        onClose={closeModal}
        title={creating ? 'New product' : 'Edit product'}
        description="Retail product details for POS and inventory."
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="product-name" className="mb-1.5 block text-sm font-medium">
              Name
            </label>
            <input
              id="product-name"
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="product-sku" className="mb-1.5 block text-sm font-medium">
                SKU
              </label>
              <input
                id="product-sku"
                type="text"
                value={form.sku}
                onChange={(e) => updateField('sku', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 font-mono text-sm uppercase',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>
            <div>
              <label htmlFor="product-barcode" className="mb-1.5 block text-sm font-medium">
                Barcode
              </label>
              <input
                id="product-barcode"
                type="text"
                value={form.barcode}
                onChange={(e) => updateField('barcode', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 font-mono text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="product-cost" className="mb-1.5 block text-sm font-medium">
                Cost
              </label>
              <input
                id="product-cost"
                type="number"
                min={0}
                step="0.01"
                value={form.cost}
                onChange={(e) => updateField('cost', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
            <div>
              <label htmlFor="product-sell" className="mb-1.5 block text-sm font-medium">
                Sell price
              </label>
              <input
                id="product-sell"
                type="number"
                min={0}
                step="0.01"
                value={form.sell_price}
                onChange={(e) => updateField('sell_price', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
            <div>
              <label htmlFor="product-min-stock" className="mb-1.5 block text-sm font-medium">
                Min stock
              </label>
              <input
                id="product-min-stock"
                type="number"
                min={0}
                value={form.min_stock}
                onChange={(e) => updateField('min_stock', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="rounded border-border"
            />
            Product is active
          </label>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : creating ? 'Create product' : 'Save product'}
            </Button>
          </div>
        </form>
      </MasterFormModal>
    </div>
  )
}
